import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Download,
  Print,
  AccessTime,
  Person,
  CalendarToday,
  AttachMoney,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface TimesheetEntry {
  date: string;
  project: string;
  startTime: string;
  endTime: string;
  breakTime: number;
  totalHours: number;
  hourlyRate: number;
  overtime: number;
  description: string;
}

interface TimesheetDetail {
  _id: string;
  employee: {
    firstName: string;
    lastName: string;
    email: string;
    position: string;
    avatar?: string;
  };
  weekEnding: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  entries: TimesheetEntry[];
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalAmount: number;
  submittedAt?: string;
  approvedAt?: string;
  approvedBy?: {
    firstName: string;
    lastName: string;
  };
  notes?: string;
}

const TimesheetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [timesheet, setTimesheet] = useState<TimesheetDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTimesheet = async () => {
      try {
        setLoading(true);
        setError(null);
        // Validate MongoDB ObjectId to avoid 400 errors
        const isValidObjectId = (value: string) => /^[a-f\d]{24}$/i.test(value);
        if (!id || !isValidObjectId(id)) {
          setTimesheet(null);
          setError('Invalid timesheet ID');
          setLoading(false);
          return;
        }
        const response = await api.get(`/timesheets/${id}`);
        const data = response.data?.data;
        setTimesheet(data || null);
      } catch (err: any) {
        console.error('Error fetching timesheet:', err);
        setError(err.response?.data?.message || 'Failed to fetch timesheet');
        setTimesheet(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTimesheet();
  }, [id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'submitted':
        return 'info';
      case 'rejected':
        return 'error';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading timesheet...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }
  if (!timesheet) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">No timesheet data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/timesheets')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold" sx={{ flex: 1 }}>
          Timesheet Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => navigate(`/timesheets/create?id=${id}`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => window.print()}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={() => window.print()}
          >
            Print
          </Button>
        </Box>
      </Box>

      {/* Employee Info Card */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={timesheet.employee.avatar}
              sx={{ width: 60, height: 60, mr: 2 }}
            >
              {timesheet.employee.firstName[0]}{timesheet.employee.lastName[0]}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {timesheet.employee.firstName} {timesheet.employee.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {timesheet.employee.position}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {timesheet.employee.email}
              </Typography>
            </Box>
            <Chip
              label={timesheet.status.charAt(0).toUpperCase() + timesheet.status.slice(1)}
              color={getStatusColor(timesheet.status) as any}
              sx={{ mr: 2 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Week Ending</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDate(timesheet.weekEnding)}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Total Hours</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {timesheet.totalRegularHours + timesheet.totalOvertimeHours} hrs
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoney fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(timesheet.totalAmount)}
                </Typography>
              </Box>
            </Box>
            
            {timesheet.approvedBy && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Approved By</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {timesheet.approvedBy.firstName} {timesheet.approvedBy.lastName}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Time Entries Table */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Time Entries
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Break (min)</TableCell>
                  <TableCell>Regular Hours</TableCell>
                  <TableCell>Overtime</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timesheet.entries.map((entry, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {new Date(entry.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </TableCell>
                    <TableCell>{entry.project}</TableCell>
                    <TableCell>{entry.startTime}</TableCell>
                    <TableCell>{entry.endTime}</TableCell>
                    <TableCell>{entry.breakTime}</TableCell>
                    <TableCell>{Math.min(entry.totalHours, 8)}</TableCell>
                    <TableCell>{entry.overtime}</TableCell>
                    <TableCell>{formatCurrency(entry.hourlyRate)}</TableCell>
                    <TableCell>
                      {formatCurrency(
                        (Math.min(entry.totalHours, 8) * entry.hourlyRate) +
                        (entry.overtime * entry.hourlyRate * 1.5)
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap title={entry.description}>
                        {entry.description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Summary
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Regular Hours</Typography>
              <Typography variant="h6" color="primary">
                {timesheet.totalRegularHours}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary">Overtime Hours</Typography>
              <Typography variant="h6" color="warning.main">
                {timesheet.totalOvertimeHours}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary">Total Amount</Typography>
              <Typography variant="h6" color="success.main">
                {formatCurrency(timesheet.totalAmount)}
              </Typography>
            </Box>
          </Box>
          
          {timesheet.notes && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" color="text.secondary">Notes</Typography>
              <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                {timesheet.notes}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TimesheetDetail;